import React, { useState } from 'react';

import { Dialog, OutlinedInput } from '@mui/material';

import Button from '../common/Button';
import { type TableType } from '../common/dataTypes';
import { toastPromise } from '../common/utils';
import { useDeleteModel, useDeleteProject } from '../hooks';
import { useDeleteDataset } from '../hooks/tanstack/useDatasetsHook';
import { useDeleteUserDefinedExpense } from '../hooks/tanstack/useExpenseHook';
import { useDeleteSharedModel } from '../hooks/tanstack/useSharedModelsHook';
import {
  useCustomNavigate,
  useGetRouteParams,
} from '../hooks/tanstack/useUtilsHook';
import routes from '../routes';

type ConfirmDeleteDialogProps = {
  open: boolean;
  handleClose: () => void;
  twoStepRequired?: boolean;
  label?: string;
  type: TableType;
  row: {
    id: number;
    name: string;
    description?: string;
    modified?: string;
  };
};

const ConfirmDeleteDialog = ({
  open,
  handleClose,
  label,
  twoStepRequired = true,
  type,
  row,
}: ConfirmDeleteDialogProps) => {
  const [confirmText, setConfirmText] = useState('');

  const { projectId, orgId } = useGetRouteParams();

  const navigate = useCustomNavigate();

  const [error, setError] = useState<{
    isError: boolean;
    message: string;
  }>({
    isError: false,
    message: '',
  });

  const { mutateAsync: deleteDataset, isPending: isDeletingDataset } =
    useDeleteDataset({
      onSuccess: () => {
        handleClose();
      },
    });

  const { mutateAsync: deleteProject, isPending: isDeletingProject } =
    useDeleteProject({
      onSuccess: () => {
        navigate(routes.projects.index, {
          orgId,
        });
        handleClose();
      },
    });

  const { mutateAsync: deleteModel, isPending: isDeletingModel } =
    useDeleteModel({
      onSuccess: () => {
        handleClose();
      },
    });

  const { mutateAsync: deleteSharedModel, isPending: isDeletingSharedModel } =
    useDeleteSharedModel({
      onSuccess: () => {
        handleClose();
      },
    });

  const { mutateAsync: deleteExpense } = useDeleteUserDefinedExpense({
    onSuccess: () => {
      handleClose();
    },
  });

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmText(e.target.value);
  };

  const handleSubmit = () => {
    if (twoStepRequired && confirmText !== row.name) {
      setError({
        isError: true,
        message: `${type} name does not match`,
      });
      return;
    }

    const id = row.id;

    if (type === 'project') {
      const res = deleteProject({
        projectId: id,
        orgId,
      });

      toastPromise({
        promise: res,
        content: `Delete ${type} ${row.name} successfully`,
      });
    } else if (type === 'dataset') {
      const res = deleteDataset({
        datasetId: id,
        orgId,
        projectId,
      });

      toastPromise({
        promise: res,
        content: `Delete ${type} ${row.name} successfully`,
      });
    } else if (type === 'model') {
      const res = deleteModel({
        modelId: id,
        orgId,
        projectId,
      });

      toastPromise({
        promise: res,
        content: `Delete ${type} ${row.name} successfully`,
      });
    } else if (type === 'expense') {
      const res = deleteExpense({
        orgId,
        projectId,
        expenseId: id,
      });

      toastPromise({
        promise: res,
        content: `Delete ${type} ${row.name} successfully`,
      });
    } else if (type === 'sharedModel') {
      const res = deleteSharedModel({
        modelId: id,
        orgId,
        projectId,
      });

      toastPromise({
        promise: res,
        content: `Delete ${type} ${row.name} successfully`,
      });
    }
  };

  const isLoading =
    isDeletingDataset ||
    isDeletingProject ||
    isDeletingModel ||
    isDeletingSharedModel;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isLoading) {
          return;
        }

        handleClose();
      }}
      aria-labelledby="alertTitle"
      aria-describedby="alertContent"
    >
      <div className="flex flex-col gap-6 p-6">
        <h2 className="text-xl font-bold">
          Are you sure you want to delete this {type}?
        </h2>
        {twoStepRequired && (
          <>
            <p className="text-[#666]">
              Type "<span className="font-bold">{row.name}</span>" to delete.
              You will not be able to recover it once it deleted.
            </p>
            <div>
              <OutlinedInput fullWidth size="small" onChange={handleOnChange} />
              {error && (
                <p className="w-full pt-2 text-start text-[14px] capitalize text-[#FF816A]">
                  {error.message as string}
                </p>
              )}
            </div>
          </>
        )}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleClose}
            variant="outlined"
            tabIndex={3}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            tabIndex={2}
            loading={isLoading}
            disabled={isLoading}
            variant="contained_red"
          >
            Yes, delete it
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
